import DialogContent from '@mui/material/DialogContent';
import { filtersSelectors } from 'app-components/flux/filters';
import { rootUiActions } from 'app-components/flux/root-ui';
import StdDialog from 'components/Dialogs/StdDialog';
import useQDialogs from 'components/QDialogs/useQDialogs';
import QIllustration from 'components/QIllustration';
import WatchlistSuccessModal from 'containers/SpendingWatchlist/components/WatchlistSuccessModal';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import TypeSelector from './TypeSelector';
import WatchlistForm from './WatchlistForm';

const useStyles = makeStyles()(() => ({
  root: {
    position: 'relative',
    minHeight: 490,
  },
  headlineIcon: {
    position: 'absolute',
  },
}));

const typesData = Object.freeze({
  CHARTOFACCOUNT: ['Category', 'Categories', 'categories'],
  PAYEE: ['Payee', 'Payees', 'payeeNames'],
  TAG: ['Tag', 'Tags', 'tags'],
});

function WatchlistModal(props) {
  const { watchlist = null, replaceURL } = props;
  const { classes } = useStyles();

  const dispatch = useDispatch();

  const [initialType, setInitialType] = useState(null);
  const [formData, setFormData] = useState(null);
  const [overwriteInitialData, setOverwriteInitialData] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const setFormDataWrapper = useCallback(
    (updatedFormData) => {
      if (
        !formData ||
        formData.dirty !== updatedFormData.dirty ||
        formData.isSubmitting !== updatedFormData.isSubmitting ||
        formData.isValid !== updatedFormData.isValid ||
        formData.isValidating !== updatedFormData.isValidating ||
        formData.resetForm !== updatedFormData.resetForm ||
        formData.submitForm !== updatedFormData.submitForm ||
        formData.values !== updatedFormData.values
      ) {
        setFormData(updatedFormData);
      }
    },
    [formData],
  );

  const filters = useSelector(filtersSelectors.getFilters);
  const { dialogAlert } = useQDialogs();
  const navigate = useNavigate();

  const isEditing = useMemo(() => watchlist !== null, [watchlist]);
  const filter = useMemo(
    () => (isEditing ? filters.find((x) => x.id === watchlist.filterId) : initialType),
    [isEditing, filters, watchlist, initialType],
  );
  const showTypeSelector = useMemo(() => !isEditing && filter === null, [isEditing, filter]);
  const showBackButton = useMemo(() => !isEditing && filter !== null, [isEditing, filter]);

  const displayDiscardAlert = useCallback(
    (callback) => {
      dialogAlert(
        'Discard changes',
        'Are you sure you want discard all the changes?',
        (btnObj) => {
          if (btnObj.btnPressed === 'Discard') {
            callback();
          }
        },
        ['Go Back', 'Discard'],
      );
    },
    [dialogAlert],
  );

  const onHandleClose = useCallback(
    (event, reason) => {
      const closeCb = () => {
        setInitialType(null);
        setFormData(null);
        setOverwriteInitialData(null);
        dispatch(rootUiActions.removeDialog('WATCHLIST_DIALOG'));

        if (replaceURL) {
          navigate('/watchlist', { replace: true });
        }
      };

      if (
        filter !== null &&
        formData?.dirty &&
        !['Enter', 'DATA_SAVED'].includes(reason) &&
        reason !== 'success-modal'
      ) {
        displayDiscardAlert(closeCb);
        return;
      }

      if (['Enter'].includes(reason)) {
        if (formData.isValid) {
          formData.submitForm();
        }
        return;
      }

      closeCb();
    },
    [dispatch, setInitialType, formData, setFormData, filter, displayDiscardAlert, navigate, replaceURL],
  );

  const onTypeClick = useCallback((type) => setInitialType(type), [setInitialType]);

  const onHandleBack = useCallback(() => {
    if (filter !== null && formData.dirty) {
      setOverwriteInitialData(formData.values);
    }

    onTypeClick(null);
  }, [onTypeClick, setOverwriteInitialData, filter, formData]);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && showBackButton) {
        onHandleClose(event, 'Enter');
      } else if (event.key === 'Escape') {
        onHandleClose(event, 'Escape');
      }
    },
    [onHandleClose, showBackButton],
  );

  const memoForm = useMemo(
    () =>
      filter ? (
        <WatchlistForm
          typesData={typesData}
          watchlist={watchlist}
          isEditing={isEditing}
          filter={filter}
          // onClose={onHandleClose}
          setFormData={setFormDataWrapper}
          overwriteInitialData={overwriteInitialData}
          setSuccessModal={setSuccessModal}
        />
      ) : null,
    [watchlist, isEditing, filter, setFormDataWrapper, overwriteInitialData],
  );

  return (
    <>
      {successModal && (
        <WatchlistSuccessModal
          open
          onClose={onHandleClose}
          name={formData?.values?.name || ''}
          created={successModal === 'create'}
        />
      )}
      <StdDialog
        onBack={onHandleBack}
        onClose={onHandleClose}
        onKeyDown={onKeyDown}
        open={successModal === null}
        showBackButton={showBackButton}
        showCloseButton
        size='md'
        title={`${isEditing ? 'Edit' : 'Create a'} Watchlist`}
      >
        <DialogContent className={classes.root}>
          <div className={classes.headlineIcon}>
            <QIllustration name='watchlistNew' size='lg' />
          </div>

          {/* Create Watchlist - Type Selector */}
          {showTypeSelector && <TypeSelector typesData={typesData} onTypeClick={onTypeClick} />}

          {/* Create/Edit Watchlist - Form */}
          {memoForm}
        </DialogContent>
      </StdDialog>
    </>
  );
}

WatchlistModal.propTypes = {
  watchlist: PropTypes.object,
  replaceURL: PropTypes.bool,
};

WatchlistModal.whyDidYouRender = true;

export default WatchlistModal;
